// src/components/ProductCard.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { getVariantPrice } from "../utils/pricing";
import "../styles/ProductCard.css";

const ProductCard = ({ product }) => {
    const { addToCart } = useCart();
    const { currency, formatPrice, fromPrice } = useCurrency();
    const navigate = useNavigate();
    const [imageError, setImageError] = useState(false);
    const [imgLoaded, setImgLoaded] = useState(false);

    // Check if the product uses the new advanced pricing matrix
    const hasMatrix = product.pricingMatrix && Object.keys(product.pricingMatrix).length > 0;

    const sortLengths = (lengths) => {
        if (!lengths) return [];
        return [...lengths].sort((a, b) => (parseInt(a.length) || 0) - (parseInt(b.length) || 0));
    };

    const initialColor = product.colors && product.colors.length > 0 ? product.colors[0] : null;
    const initialLengths = initialColor ? sortLengths(initialColor.lengths) : sortLengths(product.variants);

    const [selectedColor, setSelectedColor] = useState(initialColor);
    const [selectedLength, setSelectedLength] = useState(initialLengths.length > 0 ? initialLengths[0] : null);

    // Legacy products (dropdowns on the card) use the same keys the server understands
    const legacyKey = selectedColor
        ? `${selectedColor.name}|${selectedLength?.length ?? ''}||`
        : selectedLength ? `|${selectedLength.length}||` : '|||';

    const legacyUnitPrice = getVariantPrice(product, legacyKey, currency);

    const handleAddToCart = (e) => {
        e.preventDefault();
        if (legacyUnitPrice === null) return;

        let cartName = product.name;
        if (selectedColor && selectedColor.name !== 'Standard') cartName += ` (${selectedColor.name})`;
        if (selectedLength) cartName += ` - ${selectedLength.length}`;

        const itemToAdd = {
            id: `${product.id}__${legacyKey}`,
            productId: product.id,
            variantKey: legacyKey,
            variantLabel: [selectedColor?.name !== 'Standard' ? selectedColor?.name : '', selectedLength?.length].filter(Boolean).join(' · '),
            name: cartName,
            image: getImageSrc(),
            category: product.category,
            length: selectedLength?.length,
            priceGBP: getVariantPrice(product, legacyKey, 'GBP'),
            priceUSD: getVariantPrice(product, legacyKey, 'USD'),
        };

        addToCart(itemToAdd);
        alert(`${itemToAdd.name} has been added to your cart!`);
    };

    const handleCardClick = () => {
        navigate(`/product/${product.id}`);
    };

    const handleActionsClick = (e) => {
        e.stopPropagation();
    };

    const handleImageError = () => setImageError(true);

    const getImageSrc = () => {
        if (imageError) return "https://placehold.co/500x700";

        // 1. Check the NEW matrix system first (Matches what the Product Page does)
        if (hasMatrix && product.optionColors && product.optionColors.length > 0) {
            const defaultColor = product.optionColors[0];
            if (product.colorImages && product.colorImages[defaultColor]) {
                return product.colorImages[defaultColor];
            }
        }

        // 2. Check the OLD legacy color system
        if (selectedColor && selectedColor.image) return selectedColor.image;

        // 3. Check the Default Image array (This is where the broken blue box was hiding!)
        if (product.images && product.images.length > 0) return product.images[0];

        // 4. Ultimate fallback
        if (product.image) return product.image;

        return "https://placehold.co/500x700";
    };

    const currentLengths = selectedColor ? sortLengths(selectedColor.lengths) : sortLengths(product.variants);

    // --- SMART PRICING CALCULATOR ---
    // Lowest price in the visitor's currency (handles matrix + legacy products).
    // null = not sold in this currency (e.g. no $ prices set yet).
    const startingPrice = fromPrice(product);
    const soldInCurrency = startingPrice !== null;

    // Only show the dropdowns on the card IF it's an old legacy product without a matrix
    const showLegacyDropdowns = !hasMatrix && (product.colors?.length > 0 || product.variants?.length > 0);

    return (
        <div className="product-card-v2" onClick={handleCardClick}>
            {/* The temporary dark placeholder while ImgBB loads */}
            {!imgLoaded && !imageError && (
                <div style={{
                    width: '100%', height: '100%', position: 'absolute',
                    backgroundColor: '#0a0a0a', zIndex: 1
                }} />
            )}

            <img
                src={getImageSrc()}
                alt={product.name}
                className="card-image"
                onError={handleImageError}
                loading="lazy"
                onLoad={() => setImgLoaded(true)}
                style={{
                    opacity: imgLoaded ? 1 : 0,
                    transition: 'opacity 0.5s ease-in-out'
                }}
            />

            <div className="card-overlay" />

            <div className="card-info">
                <p className="card-title">{product.name}</p>
                <p className="card-price">
                    {soldInCurrency
                        ? `From ${formatPrice(startingPrice)}`
                        : (currency === 'USD' ? 'UK delivery only' : '')}
                </p>

                <div className="card-actions" onClick={handleActionsClick}>
                    {showLegacyDropdowns ? (
                        <>
                            {product.colors && product.colors.length > 0 && (
                                <select
                                    className="luxury-select-sm"
                                    value={selectedColor?.name || ""}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        const color = product.colors.find(c => c.name === e.target.value);
                                        setSelectedColor(color);
                                        const newSortedLengths = sortLengths(color?.lengths);
                                        setSelectedLength(newSortedLengths[0] || null);
                                    }}
                                >
                                    {product.colors.map((c) => (
                                        <option key={c.name} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                            )}

                            {currentLengths.length > 0 && (
                                <select
                                    className="luxury-select-sm"
                                    value={selectedLength?.length || ""}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        const len = currentLengths.find(l => l.length === e.target.value);
                                        setSelectedLength(len);
                                    }}
                                >
                                    {currentLengths.map((l) => {
                                        const key = selectedColor ? `${selectedColor.name}|${l.length}||` : `|${l.length}||`;
                                        const p = getVariantPrice(product, key, currency);
                                        return (
                                            <option key={l.length} value={l.length}>
                                                {l.length} — {p !== null ? formatPrice(p) : 'Unavailable'}
                                            </option>
                                        );
                                    })}
                                </select>
                            )}

                            <button className="add-btn" onClick={handleAddToCart} disabled={product.soldOut || legacyUnitPrice === null}>
                                {product.soldOut ? "Sold Out" : (legacyUnitPrice === null ? "Unavailable" : "Add to Cart")}
                            </button>
                        </>
                    ) : (
                        <button
                            className="add-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCardClick();
                            }}
                            disabled={product.soldOut}
                        >
                            {product.soldOut ? "Sold Out" : "Select Options"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductCard;